import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const BRAND = 'XPAND Agent';
const SITE_URL = 'https://xpandagent.io';
const OG_IMAGE = `${SITE_URL}/og-image.png`;

interface PageSEOData {
  title: { en: string; es: string };
  description: { en: string; es: string };
  path: string;
  jsonLd?: object;
}

const seoData: Record<string, PageSEOData> = {
  home: {
    title: {
      en: `Never Miss a Call Again - AI Voice Agents | ${BRAND}`,
      es: `Nunca Pierdas una Llamada - Agentes IA de Voz | ${BRAND}`,
    },
    description: {
      en: 'Deploy AI voice agents that answer calls, book appointments, and qualify leads 24/7. The world\'s first AI agent marketplace with 100+ ready-made agents for business automation.',
      es: 'Despliega agentes de voz IA que responden llamadas, reservan citas y califican leads 24/7. El primer marketplace de agentes IA del mundo con más de 100 agentes listos.',
    },
    path: '/',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: BRAND,
      url: SITE_URL,
      description: 'The world\'s first AI agent marketplace. Deploy ready-made AI agents for business automation.',
      potentialAction: {
        '@type': 'SearchAction',
        target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/store?q={search_term_string}` },
        'query-input': 'required name=search_term_string',
      },
    },
  },
  pricing: {
    title: {
      en: `Pricing & Plans - AI Automation Solutions | ${BRAND}`,
      es: `Precios y Planes - Soluciones de Automatización IA | ${BRAND}`,
    },
    description: {
      en: 'Compare XPAND Agent pricing plans. From free AI agents to enterprise solutions. Voice AI, Smart CRM, booking automation, and more. Start free, scale as you grow.',
      es: 'Compara planes de precios de XPAND Agent. Desde agentes IA gratuitos hasta soluciones empresariales. Voz IA, CRM inteligente, automatización de reservas y más.',
    },
    path: '/pricing',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `${BRAND} Plans`,
      description: 'AI agent marketplace plans for businesses of all sizes.',
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '0',
        highPrice: '999',
        priceCurrency: 'USD',
        offerCount: '100+',
      },
    },
  },
  store: {
    title: {
      en: `AI Agent Store - Browse 100+ Ready-Made Agents | ${BRAND}`,
      es: `Tienda de Agentes IA - Más de 100 Agentes | ${BRAND}`,
    },
    description: {
      en: 'Browse and deploy 100+ AI agents for sales, customer support, scheduling, marketing, and operations. Filter by industry, category, and pricing. Try free demos instantly.',
      es: 'Explora y despliega más de 100 agentes IA para ventas, soporte, programación, marketing y operaciones. Filtra por industria, categoría y precio.',
    },
    path: '/store',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'AI Agent Store',
      description: 'Browse 100+ ready-made AI agents for business automation.',
      url: `${SITE_URL}/store`,
    },
  },
  voiceAi: {
    title: {
      en: `AI Voice Agents - 24/7 Phone Automation | ${BRAND}`,
      es: `Agentes de Voz IA - Automatización Telefónica 24/7 | ${BRAND}`,
    },
    description: {
      en: 'Deploy AI voice agents that answer calls, book appointments, and qualify leads 24/7. Human-like conversations in 30+ languages. Try a free demo today.',
      es: 'Despliega agentes de voz IA que responden llamadas, reservan citas y califican leads 24/7. Conversaciones humanas en más de 30 idiomas.',
    },
    path: '/platform/voice-ai',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'XPAND Voice AI',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web Browser',
      description: 'AI-powered voice agents for 24/7 phone automation.',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    },
  },
  smartCrm: {
    title: {
      en: `Smart CRM - AI-Powered Customer Management | ${BRAND}`,
      es: `CRM Inteligente - Gestión de Clientes con IA | ${BRAND}`,
    },
    description: {
      en: 'Manage customer relationships with AI-powered CRM. Automatic lead scoring, smart follow-ups, pipeline automation, and real-time analytics. No manual data entry.',
      es: 'Gestiona relaciones con clientes con CRM potenciado por IA. Scoring automático, seguimientos inteligentes, automatización de pipeline y analíticas en tiempo real.',
    },
    path: '/platform/smart-crm',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'XPAND Smart CRM',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web Browser',
      description: 'AI-powered CRM with automatic lead scoring and pipeline automation.',
    },
  },
  smartBooking: {
    title: {
      en: `Smart Booking - AI Appointment Scheduling | ${BRAND}`,
      es: `Reservas Inteligentes - Programación con IA | ${BRAND}`,
    },
    description: {
      en: 'Automate appointment scheduling with AI. Smart calendar management, automated reminders, no-show prevention, and multi-channel booking. Works with your existing tools.',
      es: 'Automatiza la programación de citas con IA. Gestión inteligente de calendario, recordatorios automáticos, prevención de ausencias y reservas multicanal.',
    },
    path: '/platform/smart-booking',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'XPAND Smart Booking',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web Browser',
      description: 'AI-powered appointment scheduling and calendar management.',
    },
  },
  motionOs: {
    title: {
      en: `XPAND OS - AI Memory & Enterprise Control Platform | ${BRAND}`,
      es: `XPAND OS - Memoria IA y Plataforma de Control Empresarial | ${BRAND}`,
    },
    description: {
      en: 'XPAND OS is your enterprise AI control center. Orchestrate agents, monitor performance, manage workflows, and scale operations from one unified platform.',
      es: 'XPAND OS es tu centro de control IA empresarial. Orquesta agentes, monitorea rendimiento, gestiona flujos y escala operaciones desde una plataforma unificada.',
    },
    path: '/platform/xpand-os',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'XPAND OS',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web Browser',
      description: 'Enterprise AI control platform for orchestrating agents and workflows.',
    },
  },
  agentMarketplace: {
    title: {
      en: `AI Agent Marketplace - Deploy Agents in Minutes | ${BRAND}`,
      es: `Marketplace de Agentes IA - Despliega en Minutos | ${BRAND}`,
    },
    description: {
      en: 'Discover the XPAND Agent Hub — browse, compare, and deploy AI agents for any business function. Sales, support, marketing, scheduling, and more.',
      es: 'Descubre el XPAND Agent Hub — explora, compara y despliega agentes IA para cualquier función empresarial. Ventas, soporte, marketing, programación y más.',
    },
    path: '/platform/agent-hub',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'AI Agent Hub',
      description: 'Browse and deploy AI agents for any business function.',
      url: `${SITE_URL}/platform/agent-hub`,
    },
  },
  blog: {
    title: {
      en: `AI Insights & Resources - Expert Blog | ${BRAND}`,
      es: `Recursos e Información sobre IA - Blog Experto | ${BRAND}`,
    },
    description: {
      en: 'Expert insights on AI agents, business automation, voice AI, and digital transformation. Guides, case studies, and industry trends from XPAND Agent.',
      es: 'Información experta sobre agentes IA, automatización empresarial, voz IA y transformación digital. Guías, casos de estudio y tendencias.',
    },
    path: '/blog',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: `${BRAND} Blog`,
      description: 'Expert insights on AI agents and business automation.',
      url: `${SITE_URL}/blog`,
      publisher: { '@type': 'Organization', name: BRAND, url: SITE_URL },
    },
  },
  documentation: {
    title: {
      en: `Documentation & Guides - Developer Resources | ${BRAND}`,
      es: `Documentación y Guías - Recursos para Desarrolladores | ${BRAND}`,
    },
    description: {
      en: 'Complete documentation for XPAND Agent platform. API guides, integration tutorials, setup instructions, and best practices for AI agent deployment.',
      es: 'Documentación completa de la plataforma XPAND Agent. Guías de API, tutoriales de integración, instrucciones de configuración y mejores prácticas.',
    },
    path: '/docs',
  },
  schedule: {
    title: {
      en: `Schedule a Demo - See AI in Action | ${BRAND}`,
      es: `Agenda una Demo - Ve la IA en Acción | ${BRAND}`,
    },
    description: {
      en: 'Book a personalized demo of XPAND Agent. See how AI voice agents, Smart CRM, and automation can transform your business. Free 30-minute consultation.',
      es: 'Reserva una demo personalizada de XPAND Agent. Descubre cómo los agentes de voz IA y la automatización pueden transformar tu negocio.',
    },
    path: '/schedule',
  },
  contactSales: {
    title: {
      en: `Contact Sales - Enterprise AI Solutions | ${BRAND}`,
      es: `Contactar Ventas - Soluciones IA Empresariales | ${BRAND}`,
    },
    description: {
      en: 'Contact XPAND Agent sales team for enterprise AI solutions. Custom pricing, dedicated support, and tailored AI agent deployments for large organizations.',
      es: 'Contacta al equipo de ventas de XPAND Agent para soluciones IA empresariales. Precios personalizados, soporte dedicado y despliegues a medida.',
    },
    path: '/contact-sales',
  },
  liveDemo: {
    title: {
      en: `Live AI Demo - Try Voice Agents Now | ${BRAND}`,
      es: `Demo en Vivo - Prueba Agentes de Voz Ahora | ${BRAND}`,
    },
    description: {
      en: 'Try XPAND Agent live demo. Experience AI voice agents in real-time — make a test call, chat with AI, and see how automation works for your business.',
      es: 'Prueba la demo en vivo de XPAND Agent. Experimenta agentes de voz IA en tiempo real — haz una llamada de prueba y ve cómo funciona la automatización.',
    },
    path: '/live-demo',
  },
  hospitality: {
    title: {
      en: `AI for Restaurants & Hotels - Hospitality Solutions | ${BRAND}`,
      es: `IA para Restaurantes y Hoteles - Hostelería | ${BRAND}`,
    },
    description: {
      en: 'AI solutions for hospitality businesses. Automate reservations, manage guest communications, handle phone orders, and boost customer satisfaction with XPAND Agent.',
      es: 'Soluciones IA para hostelería. Automatiza reservas, gestiona comunicaciones con huéspedes, maneja pedidos telefónicos y mejora la satisfacción del cliente.',
    },
    path: '/solutions/hospitality',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI for Hospitality',
      provider: { '@type': 'Organization', name: BRAND },
      description: 'AI automation solutions for restaurants, cafes, and hospitality businesses.',
      areaServed: 'Worldwide',
      serviceType: 'AI Business Automation',
    },
  },
  hotels: {
    title: {
      en: `AI for Hotels - Guest Experience Automation | ${BRAND}`,
      es: `IA para Hoteles - Automatización de Experiencia | ${BRAND}`,
    },
    description: {
      en: 'Transform hotel operations with AI. Automate guest check-in, concierge services, room service orders, and multi-language support. Boost RevPAR and guest satisfaction.',
      es: 'Transforma operaciones hoteleras con IA. Automatiza check-in, servicios de conserjería, pedidos de room service y soporte multilingüe.',
    },
    path: '/solutions/hotels',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI for Hotels',
      provider: { '@type': 'Organization', name: BRAND },
      description: 'AI-powered guest experience automation for hotels and resorts.',
      serviceType: 'Hotel Automation',
    },
  },
  realEstate: {
    title: {
      en: `AI for Real Estate - Property Automation | ${BRAND}`,
      es: `IA para Inmobiliarias - Automatización Inmobiliaria | ${BRAND}`,
    },
    description: {
      en: 'AI agents for real estate professionals. Automate lead qualification, schedule property viewings, follow up with buyers, and manage listings 24/7.',
      es: 'Agentes IA para profesionales inmobiliarios. Automatiza calificación de leads, programa visitas, da seguimiento a compradores y gestiona listados 24/7.',
    },
    path: '/solutions/real-estate',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI for Real Estate',
      provider: { '@type': 'Organization', name: BRAND },
      description: 'AI-powered lead qualification and property management automation.',
      serviceType: 'Real Estate Automation',
    },
  },
  healthcare: {
    title: {
      en: `AI for Healthcare - Medical Practice Automation | ${BRAND}`,
      es: `IA para Salud - Automatización de Clínicas | ${BRAND}`,
    },
    description: {
      en: 'HIPAA-aware AI solutions for healthcare. Automate patient scheduling, appointment reminders, intake forms, and after-hours call handling for medical practices.',
      es: 'Soluciones IA para salud. Automatiza programación de pacientes, recordatorios de citas, formularios de ingreso y atención de llamadas fuera de horario.',
    },
    path: '/solutions/healthcare',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI for Healthcare',
      provider: { '@type': 'Organization', name: BRAND },
      description: 'AI automation for medical practices, clinics, and healthcare providers.',
      serviceType: 'Healthcare Automation',
    },
  },
  beauty: {
    title: {
      en: `AI for Salons & Spas - Beauty Business Solutions | ${BRAND}`,
      es: `IA para Salones y Spas - Soluciones de Belleza | ${BRAND}`,
    },
    description: {
      en: 'AI booking and customer management for salons and spas. Automated appointment scheduling, client reminders, waitlist management, and review collection.',
      es: 'Gestión de reservas y clientes con IA para salones y spas. Programación automática, recordatorios, gestión de listas de espera y recopilación de reseñas.',
    },
    path: '/solutions/beauty-salons',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI for Beauty & Wellness',
      provider: { '@type': 'Organization', name: BRAND },
      description: 'AI-powered booking and client management for salons and spas.',
      serviceType: 'Beauty Business Automation',
    },
  },
  travel: {
    title: {
      en: `AI for Travel & Tourism - Booking Automation | ${BRAND}`,
      es: `IA para Turismo - Automatización de Reservas | ${BRAND}`,
    },
    description: {
      en: 'AI solutions for travel agencies and tour operators. Automate booking inquiries, itinerary planning, customer support, and multi-language communications.',
      es: 'Soluciones IA para agencias de viajes y operadores turísticos. Automatiza consultas de reservas, planificación de itinerarios y soporte multilingüe.',
    },
    path: '/solutions/travel-tourism',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI for Travel & Tourism',
      provider: { '@type': 'Organization', name: BRAND },
      description: 'AI-powered booking automation for travel agencies and tour operators.',
      serviceType: 'Travel Automation',
    },
  },
  legal: {
    title: {
      en: `AI for Legal & Finance - Professional Services | ${BRAND}`,
      es: `IA para Legal y Finanzas - Servicios Profesionales | ${BRAND}`,
    },
    description: {
      en: 'AI automation for law firms and financial services. Client intake, appointment scheduling, document processing, and secure communication management.',
      es: 'Automatización IA para bufetes y servicios financieros. Registro de clientes, programación de citas, procesamiento de documentos y comunicaciones seguras.',
    },
    path: '/solutions/legal-finance',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI for Legal & Finance',
      provider: { '@type': 'Organization', name: BRAND },
      description: 'AI automation for law firms and financial service providers.',
      serviceType: 'Professional Services Automation',
    },
  },
  sales: {
    title: {
      en: `AI for Sales Teams - Lead Automation | ${BRAND}`,
      es: `IA para Equipos de Ventas - Automatización de Leads | ${BRAND}`,
    },
    description: {
      en: 'Supercharge your sales team with AI agents. Automated lead qualification, outbound calling, CRM updates, follow-up sequences, and pipeline management.',
      es: 'Potencia tu equipo de ventas con agentes IA. Calificación automática de leads, llamadas salientes, actualización de CRM y gestión de pipeline.',
    },
    path: '/solutions/sales',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI for Sales',
      provider: { '@type': 'Organization', name: BRAND },
      description: 'AI-powered lead qualification and sales pipeline automation.',
      serviceType: 'Sales Automation',
    },
  },
  marketing: {
    title: {
      en: `AI for Marketing - Campaign Automation | ${BRAND}`,
      es: `IA para Marketing - Automatización de Campañas | ${BRAND}`,
    },
    description: {
      en: 'AI marketing automation for businesses. Content generation, campaign management, lead nurturing, social media automation, and performance analytics.',
      es: 'Automatización de marketing con IA. Generación de contenido, gestión de campañas, nurturing de leads, automatización de redes sociales y analíticas.',
    },
    path: '/solutions/marketing',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI for Marketing',
      provider: { '@type': 'Organization', name: BRAND },
      description: 'AI-powered marketing campaign automation and analytics.',
      serviceType: 'Marketing Automation',
    },
  },
  technology: {
    title: {
      en: `AI for Technology & SaaS - Scale Operations | ${BRAND}`,
      es: `IA para Tecnología y SaaS - Escala Operaciones | ${BRAND}`,
    },
    description: {
      en: 'AI agents for tech companies and SaaS platforms. Automate customer onboarding, technical support, billing inquiries, and product demos at scale.',
      es: 'Agentes IA para empresas tech y plataformas SaaS. Automatiza onboarding, soporte técnico, consultas de facturación y demos de producto a escala.',
    },
    path: '/solutions/technology',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI for Technology & SaaS',
      provider: { '@type': 'Organization', name: BRAND },
      description: 'AI automation for tech companies and SaaS platforms.',
      serviceType: 'Technology Automation',
    },
  },
  becomeSeller: {
    title: {
      en: `Become a Seller - Sell AI Agents | ${BRAND}`,
      es: `Conviértete en Vendedor - Vende Agentes IA | ${BRAND}`,
    },
    description: {
      en: 'Sell your AI agents on XPAND Agent marketplace. Reach thousands of businesses, set your own prices, and earn recurring revenue. Apply to become a seller today.',
      es: 'Vende tus agentes IA en el marketplace de XPAND Agent. Llega a miles de negocios, establece tus precios y gana ingresos recurrentes.',
    },
    path: '/become-seller',
  },
  partners: {
    title: {
      en: `Partner Program - Grow with AI | ${BRAND}`,
      es: `Programa de Socios - Crece con IA | ${BRAND}`,
    },
    description: {
      en: 'Join the XPAND Agent partner program. Resell AI solutions, earn commissions, get dedicated support, and grow your business with cutting-edge AI technology.',
      es: 'Únete al programa de socios de XPAND Agent. Revende soluciones IA, gana comisiones, obtén soporte dedicado y crece con tecnología IA de vanguardia.',
    },
    path: '/partners',
  },
  affiliates: {
    title: {
      en: `Affiliate Program - Earn with AI | ${BRAND}`,
      es: `Programa de Afiliados - Gana con IA | ${BRAND}`,
    },
    description: {
      en: 'Earn commissions by referring businesses to XPAND Agent. Generous payouts, real-time tracking, marketing materials, and dedicated affiliate support.',
      es: 'Gana comisiones refiriendo negocios a XPAND Agent. Pagos generosos, seguimiento en tiempo real, materiales de marketing y soporte dedicado.',
    },
    path: '/affiliates',
  },
  whiteLabel: {
    title: {
      en: `White Label AI - Custom Branding Solutions | ${BRAND}`,
      es: `IA de Marca Blanca - Soluciones Personalizadas | ${BRAND}`,
    },
    description: {
      en: 'White label AI agent platform. Rebrand XPAND Agent as your own, offer AI solutions to your clients, and build a recurring revenue stream with your brand.',
      es: 'Plataforma de agentes IA de marca blanca. Rebranda XPAND Agent como tuyo, ofrece soluciones IA a tus clientes y construye ingresos recurrentes.',
    },
    path: '/white-label',
  },
  status: {
    title: {
      en: `System Status - Service Uptime | ${BRAND}`,
      es: `Estado del Sistema - Disponibilidad del Servicio | ${BRAND}`,
    },
    description: {
      en: 'Check XPAND Agent platform status. Real-time service monitoring, uptime history, and incident reports for all platform components.',
      es: 'Consulta el estado de la plataforma XPAND Agent. Monitoreo en tiempo real, historial de disponibilidad e informes de incidentes.',
    },
    path: '/status',
  },
  terms: {
    title: {
      en: `Terms of Service - Legal | ${BRAND}`,
      es: `Términos de Servicio - Legal | ${BRAND}`,
    },
    description: {
      en: 'XPAND Agent terms of service. Read our legal terms, conditions of use, user responsibilities, and service level agreements.',
      es: 'Términos de servicio de XPAND Agent. Lee nuestros términos legales, condiciones de uso, responsabilidades y acuerdos de nivel de servicio.',
    },
    path: '/terms',
  },
  privacy: {
    title: {
      en: `Privacy Policy - Data Protection | ${BRAND}`,
      es: `Política de Privacidad - Protección de Datos | ${BRAND}`,
    },
    description: {
      en: 'XPAND Agent privacy policy. Learn how we collect, use, and protect your personal data. GDPR compliant data handling and user rights.',
      es: 'Política de privacidad de XPAND Agent. Conoce cómo recopilamos, usamos y protegemos tus datos personales. Manejo de datos compatible con GDPR.',
    },
    path: '/privacy',
  },
  cookies: {
    title: {
      en: `Cookie Policy - Privacy | ${BRAND}`,
      es: `Política de Cookies - Privacidad | ${BRAND}`,
    },
    description: {
      en: 'XPAND Agent cookie policy. Understand what cookies we use, why we use them, and how to manage your cookie preferences.',
      es: 'Política de cookies de XPAND Agent. Entiende qué cookies usamos, por qué las usamos y cómo gestionar tus preferencias.',
    },
    path: '/cookies',
  },
  auth: {
    title: {
      en: `Sign In - Access Your AI Dashboard | ${BRAND}`,
      es: `Iniciar Sesión - Accede a tu Panel de IA | ${BRAND}`,
    },
    description: {
      en: 'Sign in to your XPAND Agent account. Access your AI agent dashboard, manage deployments, and monitor performance.',
      es: 'Inicia sesión en tu cuenta de XPAND Agent. Accede a tu panel de agentes IA, gestiona despliegues y monitorea rendimiento.',
    },
    path: '/auth',
  },
  dashboard: {
    title: {
      en: `Dashboard - Manage Your AI Agents | ${BRAND}`,
      es: `Panel - Gestiona tus Agentes de IA | ${BRAND}`,
    },
    description: {
      en: 'Manage your AI agents from the XPAND Agent dashboard. Monitor performance, configure settings, and track ROI.',
      es: 'Gestiona tus agentes IA desde el panel de XPAND Agent. Monitorea rendimiento, configura ajustes y rastrea ROI.',
    },
    path: '/dashboard',
  },
  account: {
    title: {
      en: `Account Settings - Profile & Preferences | ${BRAND}`,
      es: `Configuración de Cuenta - Perfil y Preferencias | ${BRAND}`,
    },
    description: {
      en: 'Manage your XPAND Agent account settings. Update your profile, billing information, notification preferences, and security settings.',
      es: 'Gestiona la configuración de tu cuenta XPAND Agent. Actualiza tu perfil, información de facturación, preferencias de notificaciones y seguridad.',
    },
    path: '/account',
  },
  checkout: {
    title: {
      en: `Checkout - Complete Your Purchase | ${BRAND}`,
      es: `Pago - Completa tu Compra | ${BRAND}`,
    },
    description: {
      en: 'Complete your AI agent purchase on XPAND Agent. Secure checkout with instant deployment.',
      es: 'Completa tu compra de agente IA en XPAND Agent. Pago seguro con despliegue instantáneo.',
    },
    path: '/checkout',
  },
  checkoutSuccess: {
    title: {
      en: `Order Confirmed - Thank You! | ${BRAND}`,
      es: `Pedido Confirmado - ¡Gracias! | ${BRAND}`,
    },
    description: {
      en: 'Your AI agent order has been confirmed. Access your new agent from your dashboard.',
      es: 'Tu pedido de agente IA ha sido confirmado. Accede a tu nuevo agente desde tu panel.',
    },
    path: '/checkout/success',
  },
  sellerDashboard: {
    title: {
      en: `Seller Dashboard - Manage Your Store | ${BRAND}`,
      es: `Panel de Vendedor - Gestiona tu Tienda | ${BRAND}`,
    },
    description: {
      en: 'Manage your XPAND Agent seller account. Track sales, manage products, and grow your AI agent business.',
      es: 'Gestiona tu cuenta de vendedor XPAND Agent. Rastrea ventas, administra productos y crece tu negocio de agentes IA.',
    },
    path: '/seller',
  },
  agentDetail: {
    title: {
      en: `AI Agent Details - Features & Pricing | ${BRAND}`,
      es: `Detalles del Agente IA - Características y Precios | ${BRAND}`,
    },
    description: {
      en: 'Explore AI agent features, pricing, reviews, and deployment options. Try a free demo before you buy.',
      es: 'Explora características, precios, reseñas y opciones de despliegue del agente IA. Prueba una demo gratuita antes de comprar.',
    },
    path: '/store',
  },
  enterpriseSchedule: {
    title: {
      en: `Enterprise Demo - Custom AI Solutions | ${BRAND}`,
      es: `Demo Empresarial - Soluciones IA Personalizadas | ${BRAND}`,
    },
    description: {
      en: 'Schedule an enterprise demo of XPAND Agent. Custom AI solutions, volume pricing, dedicated support, and tailored deployment for your organization.',
      es: 'Agenda una demo empresarial de XPAND Agent. Soluciones IA personalizadas, precios por volumen, soporte dedicado y despliegue a medida.',
    },
    path: '/enterprise',
  },
  createAgent: {
    title: {
      en: `Create AI Agent - Publish Your Agent | ${BRAND}`,
      es: `Crear Agente IA - Publica tu Agente | ${BRAND}`,
    },
    description: {
      en: 'Create and publish your AI agent on XPAND Agent marketplace.',
      es: 'Crea y publica tu agente IA en el marketplace de XPAND Agent.',
    },
    path: '/seller/create-agent',
  },
  editAgent: {
    title: {
      en: `Edit AI Agent - Update Your Listing | ${BRAND}`,
      es: `Editar Agente IA - Actualiza tu Listado | ${BRAND}`,
    },
    description: {
      en: 'Update your AI agent listing on XPAND Agent marketplace.',
      es: 'Actualiza tu listado de agente IA en el marketplace de XPAND Agent.',
    },
    path: '/seller/edit-agent',
  },
  agentChat: {
    title: {
      en: `AI Agent Chat - Try Before You Buy | ${BRAND}`,
      es: `Chat con Agente IA - Prueba Antes de Comprar | ${BRAND}`,
    },
    description: {
      en: 'Chat with an AI agent demo. Test capabilities, ask questions, and see how it works before purchasing.',
      es: 'Chatea con una demo de agente IA. Prueba capacidades, haz preguntas y ve cómo funciona antes de comprar.',
    },
    path: '/chat',
  },
  landing: {
    title: {
      en: `AI Voice Agents for Business - Never Miss a Call | ${BRAND}`,
      es: `Agentes de Voz IA para Negocios - Nunca Pierdas una Llamada | ${BRAND}`,
    },
    description: {
      en: 'AI voice agents that answer every call, book appointments, and close deals 24/7. See how XPAND Agent transforms business communications.',
      es: 'Agentes de voz IA que responden cada llamada, reservan citas y cierran tratos 24/7. Ve cómo XPAND Agent transforma las comunicaciones empresariales.',
    },
    path: '/landing',
  },
  xpandMemory: {
    title: {
      en: `XPAND OS Memory - AI Memory System | ${BRAND}`,
      es: `XPAND OS Memory - Sistema de Memoria IA | ${BRAND}`,
    },
    description: {
      en: 'XPAND OS Memory gives your AI agents persistent context. Remember customer preferences, past interactions, and business rules across every conversation.',
      es: 'XPAND OS Memory da a tus agentes IA contexto persistente. Recuerda preferencias, interacciones pasadas y reglas de negocio en cada conversación.',
    },
    path: '/platform/xpand-os/memory',
  },
  // Admin pages - no index
  adminDashboard: {
    title: { en: `Admin Dashboard | ${BRAND}`, es: `Panel Admin | ${BRAND}` },
    description: { en: 'Admin dashboard for XPAND Agent platform.', es: 'Panel de administración de XPAND Agent.' },
    path: '/admin',
  },
  adminAnalytics: {
    title: { en: `Analytics | ${BRAND}`, es: `Analíticas | ${BRAND}` },
    description: { en: 'Platform analytics.', es: 'Analíticas de plataforma.' },
    path: '/admin/analytics',
  },
  adminListings: {
    title: { en: `Agent Listings | ${BRAND}`, es: `Listados | ${BRAND}` },
    description: { en: 'Review agent submissions.', es: 'Revisar envíos.' },
    path: '/admin/listings',
  },
  adminUsers: {
    title: { en: `User Management | ${BRAND}`, es: `Gestión de Usuarios | ${BRAND}` },
    description: { en: 'User management.', es: 'Gestión de usuarios.' },
    path: '/admin/users',
  },
  adminSellers: {
    title: { en: `Seller Management | ${BRAND}`, es: `Gestión de Vendedores | ${BRAND}` },
    description: { en: 'Seller management.', es: 'Gestión de vendedores.' },
    path: '/admin/sellers',
  },
  adminOrders: {
    title: { en: `Order Management | ${BRAND}`, es: `Gestión de Pedidos | ${BRAND}` },
    description: { en: 'Order management.', es: 'Gestión de pedidos.' },
    path: '/admin/orders',
  },
  adminPayments: {
    title: { en: `Payments | ${BRAND}`, es: `Pagos | ${BRAND}` },
    description: { en: 'Payments and payouts.', es: 'Pagos y transferencias.' },
    path: '/admin/payments',
  },
  adminReviews: {
    title: { en: `Reviews | ${BRAND}`, es: `Reseñas | ${BRAND}` },
    description: { en: 'Review moderation.', es: 'Moderación de reseñas.' },
    path: '/admin/reviews',
  },
  adminTickets: {
    title: { en: `Support Tickets | ${BRAND}`, es: `Tickets | ${BRAND}` },
    description: { en: 'Support tickets.', es: 'Tickets de soporte.' },
    path: '/admin/tickets',
  },
  adminLeads: {
    title: { en: `Leads | ${BRAND}`, es: `Leads | ${BRAND}` },
    description: { en: 'Lead management.', es: 'Gestión de leads.' },
    path: '/admin/leads',
  },
  adminPartners: {
    title: { en: `Partners | ${BRAND}`, es: `Socios | ${BRAND}` },
    description: { en: 'Partner applications.', es: 'Solicitudes de socios.' },
    path: '/admin/partners',
  },
  adminAffiliates: {
    title: { en: `Affiliates | ${BRAND}`, es: `Afiliados | ${BRAND}` },
    description: { en: 'Affiliate applications.', es: 'Solicitudes de afiliados.' },
    path: '/admin/affiliates',
  },
  adminStatus: {
    title: { en: `System Status | ${BRAND}`, es: `Estado | ${BRAND}` },
    description: { en: 'System status management.', es: 'Gestión de estado.' },
    path: '/admin/status',
  },
};

const ADMIN_KEYS = [
  'adminDashboard', 'adminAnalytics', 'adminListings', 'adminUsers',
  'adminSellers', 'adminOrders', 'adminPayments', 'adminReviews',
  'adminTickets', 'adminLeads', 'adminPartners', 'adminAffiliates', 'adminStatus',
];

const NOINDEX_KEYS = [
  ...ADMIN_KEYS, 'dashboard', 'account', 'checkout', 'checkoutSuccess',
  'sellerDashboard', 'createAgent', 'editAgent', 'auth', 'agentChat',
];

export type PageSEOKey = keyof typeof seoData;

export function PageSEO({ pageKey }: { pageKey: PageSEOKey }) {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const altLang = lang === 'es' ? 'en' : 'es';
  const data = seoData[pageKey];
  if (!data) return null;

  const title = data.title[lang];
  const description = data.description[lang];
  // Canonical now includes language prefix
  const canonicalUrl = `${SITE_URL}/${lang}${data.path}`;
  const noIndex = NOINDEX_KEYS.includes(pageKey);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:locale" content={lang === 'es' ? 'es_ES' : 'en_US'} />
      <meta property="og:locale:alternate" content={altLang === 'es' ? 'es_ES' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {/* hreflang: proper language-prefixed URLs for each language */}
      <link rel="alternate" hrefLang="en" href={`${SITE_URL}/en${data.path}`} />
      <link rel="alternate" hrefLang="es" href={`${SITE_URL}/es${data.path}`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/en${data.path}`} />

      {/* JSON-LD */}
      {data.jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(data.jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

// Hook version for backward compatibility
export function usePageSEO(pageKey: PageSEOKey) {
  // The actual rendering is done by the PageSEO component
  // This hook just provides a convenient way to use it
  return { pageKey };
}

export { seoData };
